<script lang="ts">
    import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
    import ReviewsTheme from "@apps/reviews/components/ReviewsTheme/ReviewsTheme.svelte";
    import { ReviewsWidgetDefinition } from "@apps/reviews/definitions";
    import { REVIEWS_SDK_METHODS } from "@apps/reviews/reviews_sdk/constants";
    import CloseReviewsDrawerSdkMethodPayload from "@apps/reviews/reviews_sdk/methods/CloseReviewsDrawerSdkMethodPayload";
    import OpenReviewsDrawerSdkMethodPayload from "@apps/reviews/reviews_sdk/methods/OpenReviewsDrawerSdkMethodPayload";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import { reviewsFormModalToken } from "@apps/reviews/tokens";
    import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
    import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { ExtractModuleType } from "@modules/dynamic_import/types";
    import { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import WidgetClosedSdkEvent from "@modules/sdk/common_sdk/events/WidgetClosedSdkEvent";
    import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
    import WidgetOpenedSdkEvent from "@modules/sdk/common_sdk/events/WidgetOpenedSdkEvent";
    import {
        dispatchSdkEvent,
        subscribeToSdkMethod,
    } from "@modules/sdk/helpers";
    import { focusTrap } from "@ui-utils/actions/focusTrap";
    import { Observable } from "rxjs";
    import { skip, tap } from "rxjs/operators";
    import { getAllContexts, onMount } from "svelte";

    import Drawer from "@components/Drawer/Drawer.svelte";
    import DrawerManager from "@components/Drawer/DrawerManager";
    import Icon from "@components/Icon.svelte";
    import CloseIcon from "@components/icons/CloseIcon.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import Portal from "@components/Portal/Portal.svelte";
    import Widget from "@components/Widget.svelte";
    import { LoggerInterface } from "@interfaces/LoggerInterface";

    import ReviewsGalleryManager from "../reviews_gallery_widget/ReviewsGalleryManager";
    import ReviewsDrawerButton from "./components/ReviewsDrawerButton.svelte";

    export let customizationManager: CustomizationManager;
    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let widgetName: REVIEWS_WIDGET_NAMES;
    export let modalManager: ModalManager;
    export let reviewsGalleryManager: ReviewsGalleryManager;
    export let reviewsAppStatus$: Observable<MODULE_STATUS>;
    export let dynamicImportManager: DynamicImportManager;
    export let logger: LoggerInterface;
    export let i18n: GwI18n;
    export let afterWidgetInject: () => void;

    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();

    const drawerManager = new DrawerManager();
    const drawerIsVisible$ = drawerManager.visible$;

    const isReviewFormModalOpen$ = modalManager.selectIsOpen(
        reviewsFormModalToken
    );
    const isReviewsGalleryModalOpen$ = reviewsGalleryManager.isOpen$;

    const handleOpenDrawer = () => {
        drawerManager.drawerShow();
    };
    const handleCloseDrawer = () => {
        drawerManager.drawerHide();
    };

    const openReviewsDrawerSubscription =
        subscribeToSdkMethod<OpenReviewsDrawerSdkMethodPayload>(
            REVIEWS_SDK_METHODS.OPEN_REVIEWS_DRAWER,
            ({ onSuccess }) => {
                handleOpenDrawer();
                onSuccess();
            }
        );

    const closeReviewsDrawerSubscription =
        subscribeToSdkMethod<CloseReviewsDrawerSdkMethodPayload>(
            REVIEWS_SDK_METHODS.CLOSE_REVIEWS_DRAWER,
            ({ onSuccess }) => {
                handleCloseDrawer();
                onSuccess();
            }
        );

    const drawerVisibilitySubscription = drawerIsVisible$
        .pipe(
            skip(1),
            tap((isVisible) => {
                if (isVisible) {
                    dispatchSdkEvent(new WidgetOpenedSdkEvent(widgetName));
                } else {
                    dispatchSdkEvent(new WidgetClosedSdkEvent(widgetName));
                }
            })
        )
        .subscribe();

    onMount(() => {
        afterWidgetInject();

        return () => {
            openReviewsDrawerSubscription.unsubscribe();
            closeReviewsDrawerSubscription.unsubscribe();
            drawerVisibilitySubscription.unsubscribe();
        };
    });

    let ReviewsWidget:
        | ExtractModuleType<typeof ReviewsWidgetDefinition>["default"]
        | null = null;

    const importReviewsWidget = async () => {
        if (ReviewsWidget) return;

        try {
            ReviewsWidget = await dynamicImportManager.importDefault(
                ReviewsWidgetDefinition,
                true
            );
        } catch (error) {
            console.error("Failed to load WishlistsWidget module", error);
        }
    };

    $: if (
        $reviewsAppStatus$ === MODULE_STATUS.INITIALIZED &&
        $drawerIsVisible$
    ) {
        importReviewsWidget();
    }

    let focusTrapTargetRef: HTMLElement | null = null;
    let focusTrapInstance: ReturnType<typeof focusTrap>;

    const activateFocusTrap = async () => {
        if (
            $isReviewFormModalOpen$ ||
            $isReviewsGalleryModalOpen$ ||
            !focusTrapTargetRef
        ) {
            return;
        }

        await new Promise((resolve) => requestAnimationFrame(resolve));

        const widgetContainer = focusTrapTargetRef.querySelector(
            ".gw-rv-widget-container"
        );
        const reviewsWidgetRootElement: HTMLElement | null =
            widgetContainer?.shadowRoot?.querySelector(".gw-rv") || null;

        focusTrapInstance = focusTrap(focusTrapTargetRef, {
            enabled: true,
            elements: reviewsWidgetRootElement
                ? [reviewsWidgetRootElement]
                : undefined,
            options: {
                allowOutsideClick: true,
            },
        });
    };

    const handleReviewsWidgetInit = () => {
        dispatchSdkEvent(
            new WidgetInjectedSdkEvent(REVIEWS_WIDGET_NAMES.REVIEWS_WIDGET)
        );

        activateFocusTrap();
    };

    $: if (!$drawerIsVisible$) {
        focusTrapInstance?.destroy();
    }
</script>

{#if $reviewsBranding$}
    <ReviewsTheme branding={$reviewsBranding$}>
        <div bind:this={focusTrapTargetRef}>
            <Drawer
                drawerClass="gw-rv-drawer"
                direction={$reviewsBranding$.reviewsDrawerSettings
                    .drawerPosition}
                {drawerManager}
            >
                <Portal
                    order={1}
                    Component={ReviewsDrawerButton}
                    selector={`.gw-widget-placeholder-${$reviewsBranding$.reviewsDrawerSettings.drawerPosition}`}
                    {widgetName}
                    {customizationManager}
                    shadow
                    componentProps={{
                        reviewsStorefrontValuesService,
                        direction:
                            $reviewsBranding$.reviewsDrawerSettings
                                .drawerPosition,
                        i18n,
                    }}
                    componentContext={getAllContexts()}
                    componentListeners={{
                        click: handleOpenDrawer,
                        mouseenter: importReviewsWidget,
                    }}
                />

                <div class="gw-rv-drawer__sidebar" slot="sidebar">
                    <button
                        class="gw-rv-drawer-sidebar__close_btn"
                        on:click={handleCloseDrawer}
                        aria-label="Close reviews sidebar"
                    >
                        <Icon>
                            <CloseIcon />
                        </Icon>
                    </button>

                    {#if $reviewsAppStatus$ === MODULE_STATUS.INITIALIZED && ReviewsWidget && $drawerIsVisible$}
                        <Widget
                            class="gw-rv-widget-container"
                            widget={ReviewsWidget}
                            params={[
                                {
                                    isWrappedWidget: true,
                                    isWidgetShadowRootEnabled: true,
                                },
                                logger,
                            ]}
                            on:widgetInit={handleReviewsWidgetInit}
                        />
                    {/if}
                </div>
            </Drawer>
        </div>
    </ReviewsTheme>
{/if}

<style lang="postcss">
    :global {
        .gw-rv-drawer {
            color: var(--gw-theme-main-text-primary);
        }

        .gw-rv-widget-container,
        .gw-rv-widget-placeholder {
            height: 100%;
        }
    }

    .gw-rv-drawer__sidebar {
        height: 100%;
        display: flex;
        flex-direction: column;
        position: relative;
    }

    .gw-rv-drawer-sidebar__close_btn {
        position: absolute;
        top: 0;
        right: 0px;
        width: 44px;
        height: 44px;
        padding: 14px;
        border: none;
        background: transparent;
        line-height: 1;
        cursor: pointer;
    }
</style>
